import React, { createContext, useContext, useState, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase, signIn, signOut, signUp } from '../lib/supabase'
import { clearLessonDraft } from '@/lesson/lesson-draft'
import { profileService } from '../services/profile.service'
import { ProfileDTO } from '@superlekcja/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as Sentry from "@sentry/react";
import { LoginDialog } from '@/components/auth/login-dialog'
import { identifyAnalyticsUser, track } from '@/utils/analytics'
import { sendPendingAcquisition } from '@/acquisition/acquisition-source'

// Okno, w ktorym pierwsze zalogowanie liczymy jako zalozenie konta: potwierdzenie maila
// moze przyjsc wiele godzin po rejestracji.
const NEW_ACCOUNT_WINDOW_MS = 24 * 60 * 60 * 1000
const accountCreatedKey = (userId: string) => `superlekcja.account-created-tracked:${userId}`

function trackAccountCreatedOnce(user: User) {
  if (Date.now() - new Date(user.created_at).getTime() > NEW_ACCOUNT_WINDOW_MS) return
  try {
    if (localStorage.getItem(accountCreatedKey(user.id))) return
    localStorage.setItem(accountCreatedKey(user.id), '1')
  } catch {
    // Bez storage nie odroznimy pierwszego logowania od kolejnego — lepiej nie wyslac niz liczyc podwojnie.
    return
  }
  track('account_created', { method: user.app_metadata?.provider ?? 'unknown' })
}

/** Jedno miejsce, w ktorym tozsamosc trafia do narzedzi diagnostycznych i analityki. */
function applyUserIdentity(user: User | null) {
  Sentry.setUser(user ? { id: user.id } : null)
  identifyAnalyticsUser(user?.id ?? null)
  // Nieudana wysylka zostawia zrodlo w przegladarce na nastepne logowanie; blad ma byc widoczny.
  if (user) sendPendingAcquisition().catch(error => Sentry.captureException(error))
}


interface AuthContextType {
  user: User | null;
  profile: ProfileDTO | null;
  isAuthenticated: boolean;
  isTester: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isProfileLoading: boolean;
  withUser: (action: (user: User) => void) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Profile query
  const { 
    data: profile, 
    isLoading: isProfileLoading,
    refetch: refetchProfile
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) {
        return null;
      }
      // Wait a bit to ensure the session is properly set up
      //await new Promise(resolve => setTimeout(resolve, 500));
      return profileService.fetchProfile();
    },
    // Only run query if we have a user
    enabled: !!user,
    // Retry failed requests 3 times
    retry: 3,
    // Refetch on window focus
    refetchOnWindowFocus: true,
    // Refetch every 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setUser(session?.user ?? null);
          applyUserIdentity(session?.user ?? null);
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Initialize auth state
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      applyUserIdentity(session?.user ?? null);
      if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) trackAccountCreatedOnce(session.user);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    await signIn(email, password)
  }

  const signup = async (email: string, password: string) => {
    await signUp(email, password)
  }

  const logout = async () => {
    // Przed signOut: nawet nieudane wylogowanie nie moze zostawic tematu kolejnej osobie (wspolne komputery w szkole).
    clearLessonDraft()
    try {
      setIsLoading(true)
      // Invalidate and remove profile from cache
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.removeQueries({ queryKey: ['profile'] })
      await signOut()
    } catch (error) {
      console.error('Error during logout:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshProfile = async () => {
    if (user) {
      await refetchProfile({
        cancelRefetch: false,
        throwOnError: false
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  function withUser(action: (user: User) => void) {
    if (user) {
      action(user)
    } else {
      setShowAuthModal(true)
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      profile: profile ?? null,
      isAuthenticated: !!user,
      login,
      signup,
      logout,
      refreshProfile,
      isProfileLoading,
      isTester: profile?.roles?.includes('tester') ?? false,
      withUser
    }}>
      {children}
      <LoginDialog isOpen={showAuthModal} onClose={() => setShowAuthModal(false)}/>
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 